import { File, Folder, Home, List, NotebookPen, SearchIcon, X } from "lucide-react"
import { Dialog, DialogClose, DialogContent, DialogTrigger } from "@components/common/dialog"
import { useCallback, useEffect, useRef, useState } from "react"
import { DictType } from "@pma/types"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { Form, FormControl, FormField, FormItem, FormMessage } from "@components/common/form"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { Badge } from "@components/common/badge"
import PmsLoader from "@components/common/loader"
import { useAuth } from "@stores/auth-store"
import SearchResults from "./search-results-container"
import useTranslator from "@hooks/use-translator"
import { cn } from "@pma/utils/helpers"
import { getOrganizationGlobalSearch } from "@pma/utils/utils"

const formSchema = z.object({
  search_text: z.string().min(2, { message: "Please enter keyword" }),
})

interface Props {
  sm?: boolean
}
const GlobalSearch = ({ sm }: Props) => {
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState({
    pending: true,
    text: "",
    results: {
      items: [],
    },
  })
  const [activeIndex, setActiveIndex] = useState(0)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      search_text: "",
    },
  })

  const [filters, setFilters] = useState({
    in_files: 1,
    in_projects: 1,
    in_notes: 1,
    in_tasks: 1,
    paging_start_page: 1,
    paging_per_page: 25,
    paging_offset: 0,
  })
  const [distribution, setDistribution] = useState<DictType>({})

  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const [inputReady, setInputReady] = useState(false)
  const { authConfig } = useAuth()

  const orgId = authConfig?.last_organization_id

  const globalSearchMutation = useMutation({
    mutationKey: ["global-search", search?.text],
    mutationFn: (activeFilters: typeof filters) =>
      getOrganizationGlobalSearch(orgId, search.text, { ...activeFilters }),
    onSuccess: (data) => {
      setSearch((prev) => ({ ...prev, pending: false, results: data }))
    },
    onError: (error: DictType) => {
      const serverError = error?.response?.data?.message
      const emptyResponse = "Incorrect page index"
      const message = serverError === emptyResponse ? "No items found" : serverError
      setSearch((prev) => ({
        ...prev,
        pending: false,
        results: { items: [] },
      }))

      message &&
        toast({
          title: translate("Error"),
          description: translate(message),
          variant: "destructive",
        })
    },
  })
  const searchOptions = [
    { label: "All", icon: Home },
    { label: "Projects", icon: File },
    { label: "Tasks", icon: List },
    { label: "Notes", icon: NotebookPen },
    { label: "Files", icon: Folder },
  ]

  const handleTabChange = (index: number) => {
    let temp
    switch (index) {
      case 1:
        temp = {
          ...filters,
          in_files: 0,
          in_projects: 1,
          in_notes: 0,
          in_tasks: 0,
        }
        break
      case 2:
        temp = {
          ...filters,
          in_files: 0,
          in_projects: 0,
          in_notes: 0,
          in_tasks: 1,
        }
        break

      case 3:
        temp = {
          ...filters,
          in_files: 0,
          in_projects: 0,
          in_notes: 1,
          in_tasks: 0,
        }
        break
      case 4:
        temp = {
          ...filters,
          in_files: 1,
          in_projects: 0,
          in_notes: 0,
          in_tasks: 0,
        }
        break
      default:
        temp = {
          ...filters,
          in_files: 1,
          in_projects: 1,
          in_notes: 1,
          in_tasks: 1,
        }
        break
    }

    setFilters(temp)

    if (search.text) {
      globalSearchMutation.mutate(temp)
    }
    setActiveIndex(index)
  }

  useEffect(() => {
    if (inputReady && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [inputReady])

  const getDistribution = useCallback(() => {
    const tasks = search.results?.items?.filter((result: DictType) => result.type === "task")
    const projects = search.results?.items?.filter((result: DictType) => result.type === "project")
    const notes = search.results?.items?.filter((result: DictType) => result.type === "note")
    const files = search.results?.items?.filter((result: DictType) => result.type === "file")

    setDistribution({
      tasks: {
        title: "Tasks",
        items: tasks,
        count: tasks?.length,
      },
      notes: {
        title: "Notes",
        items: notes,
        count: notes?.length,
      },
      projects: {
        title: "Projects",
        items: projects,
        count: projects?.length,
      },
      files: {
        title: "Files",
        items: files,
        count: files.length,
      },
    })
  }, [search?.results?.items?.length])

  useEffect(() => {
    if (search?.results?.items) {
      getDistribution()
    }
  }, [getDistribution])

  function onSubmit(data: z.infer<typeof formSchema>) {
    globalSearchMutation.mutate({ ...filters, ...data })
  }

  const handleClose = () => {
    setIsOpen(false)
    setSearch({
      pending: true,
      text: "",
      results: { items: [] },
    })
    form.reset()
  }

  return (
    <Dialog open={isOpen}>
      <DialogTrigger className='w-full max-w-2xl' onClick={() => setIsOpen(true)}>
        <div className='relative'>
          {!sm && (
            <div className='w-full cursor-pointer rounded-full border-gray-700 bg-slate-500 py-1.5 pl-4 pr-12 text-xs text-slate-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-600'>
              {translate("Search for anything")}{" "}
            </div>
          )}
          <SearchIcon className='absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-white' />
        </div>
      </DialogTrigger>
      <DialogContent
        className={cn(
          "flex h-[90vh] w-full flex-col gap-2 overflow-hidden p-0 md:max-w-[90vw]",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        <DialogClose
          className={cn(
            "absolute top-3 z-10 text-primary transition-all ease-in-out hover:scale-110",
            isHebrew ? "left-3" : "right-3",
          )}
          onClick={handleClose}
        >
          <X />
        </DialogClose>
        <div className='flex flex-col pb-4 shadow-md'>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='text-white'>
              <div className='z-50 w-[100%] cursor-pointer rounded-t-md border-gray-700 bg-slate-300/40 p-2 text-primary'>
                {`${
                  !search.pending && search.text
                    ? translate(`Showing results for`) + " " + `"${search.text}"`
                    : translate("Search For Anything") + "..."
                }`}
              </div>
              <FormField
                control={form.control}
                name='search_text'
                render={({ field }) => (
                  <FormItem className='my-3 flex flex-col items-start justify-start space-y-2 px-2'>
                    <FormControl>
                      <input
                        placeholder={translate("Search anything and enter the key")}
                        className='w-full rounded-none border-0 border-b-2 border-b-primary bg-none text-lg font-medium text-black shadow-none focus:outline-none'
                        {...field}
                        onChange={(e) => {
                          field.onChange(e)
                          setSearch((prev) => ({
                            ...prev,
                            text: e.target.value,
                            pending: !!e.target.value,
                          }))
                        }}
                        autoFocus
                        onFocus={() => setInputReady(true)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>

          <div className='flex gap-2 px-2'>
            {searchOptions.map((option: DictType, index: number) => (
              <Badge
                key={index}
                variant={index === activeIndex ? "default" : "outline"}
                onClick={() => handleTabChange(index)}
                className='flex cursor-pointer items-center justify-center gap-2 rounded-full p-1 px-3 text-center'
              >
                {<option.icon size={16} />}
                <p className='hidden text-left md:block'>{translate(option.label)}</p>{" "}
              </Badge>
            ))}
          </div>
        </div>

        {globalSearchMutation.isPending ? (
          <div className='flex h-full w-full items-center justify-center'>
            <PmsLoader />
          </div>
        ) : (
          <div className='h-full w-full overflow-x-auto md:p-2'>
            {search?.results.items?.length > 0 && !search.pending ? (
              <>
                {!search.pending ? (
                  <SearchResults results={distribution} setIsOpen={setIsOpen} />
                ) : (
                  <>
                    <EmptyResults />
                  </>
                )}
              </>
            ) : (
              <>{search?.results?.items?.length < 1 && !search.pending && <EmptyResults />}</>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default GlobalSearch

const EmptyResults = () => {
  const { translate } = useTranslator()

  return (
    <div className='flex min-h-[30vh] w-[100%] flex-col items-center justify-end'>
      <p className='mb-4 text-lg font-medium'>{translate("Sorry we didn't find anything")}.</p>{" "}
      <p>{translate("Make sure everything is spelled correctly or try a different phrase")}. </p>
    </div>
  )
}
